import { useState } from "react";
import { Trash2, Loader2, RotateCw } from "lucide-react";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import {
  deleteCachedCalendarEvents as defaultDelete,
  refreshCachedCalendarEvents as defaultRefresh,
} from "../../../lib/data/availability";

/**
 * Cache controls — surfaces the destructive "Delete cached events" action
 * inside the Availability settings popover (instead of the prominent sidebar
 * placement it had through Phase 4c). Pairs the destructive action with a
 * "Refresh" button so a user who wipes the cache can restore it immediately.
 *
 * Why this lives in settings, not the page chrome:
 *   - The action is rarely needed.
 *   - Without context, the button reads as scary ("delete what?").
 *   - In settings, we have room for the explanatory copy that names the
 *     non-side-effects (linked calendars, marks, free-time settings, vote
 *     history all stay intact).
 *
 * Props:
 *   - eventCount: number — used to enable Delete (only when > 0) and to size
 *     the confirm-dialog copy.
 *   - linkedCalendarCount: number — used to disable Refresh when there are
 *     no linked calendars to refresh from.
 *   - onDeleted? / onRefreshed?: ({...}) => void — fire on success.
 *   - deleteFn? / refreshFn?: injection points for tests.
 */
export function CacheControlsSection({
  eventCount = 0,
  linkedCalendarCount = 0,
  onDeleted,
  onRefreshed,
  deleteFn = defaultDelete,
  refreshFn = defaultRefresh,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const [refreshError, setRefreshError] = useState(null);

  const deleteDisabled = deleting || eventCount === 0;
  const refreshDisabled = refreshing || linkedCalendarCount === 0;

  async function handleConfirmDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await deleteFn();
      setConfirmOpen(false);
      if (onDeleted) onDeleted(result);
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete cached events.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleRefresh() {
    if (refreshing || refreshDisabled) return;
    setRefreshing(true);
    setRefreshError(null);
    setRefreshMessage(null);
    try {
      const result = await refreshFn();
      const refreshed = result?.calendarsRefreshed ?? 0;
      const failed = result?.calendarsFailed ?? 0;
      const events = result?.eventsProcessed ?? 0;
      let message;
      if (failed === 0) {
        message = `Refreshed ${refreshed} calendar${refreshed === 1 ? "" : "s"} (${events} events cached).`;
      } else if (refreshed === 0) {
        message = `Couldn't refresh any calendar.`;
      } else {
        message = `Refreshed ${refreshed} of ${refreshed + failed} calendars; ${failed} failed.`;
      }
      setRefreshMessage(message);
      if (onRefreshed) onRefreshed(result);
    } catch (err) {
      setRefreshError(err?.message || "Failed to refresh cached events.");
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <section
      className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700"
      data-testid="cache-controls-section"
    >
      <div>
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Cached calendar events
        </h4>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Quest Scheduler keeps a local cache of your linked calendars so it
          can flag conflicts and auto-vote on polls. You usually don&apos;t
          need to touch this. Linked calendars, free-time marks, and your
          settings are not affected by either button.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshDisabled}
          data-testid="refresh-cached-events-button"
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          title={
            linkedCalendarCount === 0
              ? "Link a calendar first"
              : "Re-fetch the next 60 days from each linked calendar"
          }
        >
          {refreshing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Refresh cached events
        </button>

        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={deleteDisabled}
          data-testid="delete-cached-events-button"
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50/50 px-3 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-200 dark:hover:bg-rose-900/40"
          title={
            eventCount === 0
              ? "No cached events to delete"
              : `Delete all ${eventCount} cached events`
          }
        >
          {deleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          Delete cached events
          {eventCount > 0 && (
            <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-rose-700 dark:bg-rose-800/60 dark:text-rose-100">
              {eventCount}
            </span>
          )}
        </button>
      </div>

      {refreshMessage && (
        <p
          role="status"
          data-testid="refresh-cached-events-status"
          className="text-[11px] text-slate-500 dark:text-slate-400"
        >
          {refreshMessage}
        </p>
      )}
      {refreshError && (
        <p
          role="alert"
          className="text-[11px] text-rose-600 dark:text-rose-300"
        >
          {refreshError}
        </p>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (deleting) return;
          setConfirmOpen(open);
          if (!open) setDeleteError(null);
        }}
        title="Delete cached calendar events?"
        description={
          deleteError
            ? deleteError
            : `This wipes ${eventCount} cached event${
                eventCount === 1 ? "" : "s"
              } across all your linked calendars. Your Google Calendar is not affected — only Quest Scheduler's local cache. Conflict warnings will be empty until your next calendar sync (or a manual refresh) repopulates the cache.`
        }
        confirmLabel="Delete cached events"
        cancelLabel="Cancel"
        confirming={deleting}
        onConfirm={handleConfirmDelete}
        variant="destructive"
      />
    </section>
  );
}
